import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PhoneForwarded, CheckCircle2, CalendarClock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); }
  catch { return iso; }
};

// Agent's OWN two-week feedback/referral follow-ups (won deals).
const MyFollowUpsCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API}/follow-ups/mine`).then(r => setData(r.data)).catch(() => setData({ pending: [] }));
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    const note = window.prompt('Optional note (e.g. "spoke off-platform"):') || '';
    setBusy(id);
    try {
      await axios.post(`${API}/follow-ups/${id}/complete`, { note });
      toast.success('Follow-up marked complete');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete');
    } finally { setBusy(null); }
  };

  const lost = async (id) => {
    const note = window.prompt('Why is this lead lost? (optional)') || '';
    setBusy(id);
    try {
      await axios.post(`${API}/follow-ups/${id}/lost`, { note });
      toast.success('Marked as lost');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark lost');
    } finally { setBusy(null); }
  };

  const postpone = async (id) => {
    const due = window.prompt('Postpone until (YYYY-MM-DD):') || '';
    if (!due) return;
    setBusy(id);
    try {
      await axios.post(`${API}/follow-ups/${id}/postpone`, { due_date: due.trim() });
      toast.success(`Postponed to ${fmtDate(due.trim())}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not postpone');
    } finally { setBusy(null); }
  };

  if (!data || (data.pending || []).length === 0) return null;
  return (
    <Card data-testid="my-followups-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <PhoneForwarded className="w-4 h-4" /> My 2-Week Follow-ups
          <span className="text-xs font-normal text-slate-400">check-in + referral ask for your won deals</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {data.pending.map(t => (
          <div key={t.id} className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-1.5 ${t.overdue ? 'border-red-200 bg-red-50' : 'border-slate-100'}`} data-testid={`followup-row-${t.id}`}>
            <span className="text-xs">
              <span className="font-semibold text-slate-700">{t.customer_phone}</span>
              <span className="text-slate-500 ml-2">due {fmtDate(t.due_date)}</span>
              {t.overdue && <span className="ml-2 text-[10px] uppercase tracking-wider text-red-600 font-semibold">overdue</span>}
            </span>
            <span className="flex items-center gap-1">
              <button
                onClick={() => complete(t.id)}
                disabled={busy === t.id}
                className="text-[11px] inline-flex items-center gap-1 border border-emerald-300 text-emerald-700 rounded px-2 py-0.5 hover:bg-emerald-50 disabled:opacity-40"
                data-testid={`followup-complete-${t.id}`}
              >
                <CheckCircle2 className="w-3 h-3" /> Done
              </button>
              <button
                onClick={() => postpone(t.id)}
                disabled={busy === t.id}
                className="text-[11px] inline-flex items-center gap-1 border border-amber-300 text-amber-700 rounded px-2 py-0.5 hover:bg-amber-50 disabled:opacity-40"
                data-testid={`followup-postpone-${t.id}`}
              >
                <CalendarClock className="w-3 h-3" /> Postpone
              </button>
              <button
                onClick={() => lost(t.id)}
                disabled={busy === t.id}
                className="text-[11px] inline-flex items-center gap-1 border border-red-200 text-red-600 rounded px-2 py-0.5 hover:bg-red-50 disabled:opacity-40"
                data-testid={`followup-lost-${t.id}`}
              >
                <XCircle className="w-3 h-3" /> Lost
              </button>
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default MyFollowUpsCard;
